import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useGooglePlaces } from './useGooglePlaces';
import { selectDealersList, setUserSelectedLocation } from '../../reducers/dealersListReducer';
import { MARKET } from '../../constants/market';
import { ParsePlaceResult } from '../../utils/parsePlace';
import {
  StyledInput,
  SearchIconContainer,
  ClearInputWrapper,
  ErrorWrapper,
  InputFieldWrapper,
  PlaceHolderForLandscape,
} from './AddressAutoComplete.style';
import SearchIcon from '../../assets/icons/SearchIcon';
import CancelIcon from '../../assets/icons/CancelIcon';
import CautionIcon from '../../assets/icons/CautionIcon';
import SuggestionsList from './SuggestionsList';

const AddressAutoComplete: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const { centerLocation } = useSelector(selectDealersList);
  const [inputValue, setInputValue] = useState(centerLocation?.name ?? '');
  const { suggestions, fetchSuggestions, error, setError } = useGooglePlaces(
    MARKET === 'CAN' ? 'ca' : 'us',
  );
  const [focusedOptionIndex, setFocusedOptionIndex] = useState<number>(0);

  const searchTimer = useRef<number | null>(null);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;

    setInputValue(input);
    if (focusedOptionIndex > 0) setFocusedOptionIndex(0);

    // Clear previous error
    setError(null);

    // Clear previous timer
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
    }

    // Set a timer to fetch suggestions after a delay
    searchTimer.current = window.setTimeout(() => {
      fetchSuggestions(input);
    }, 400);
  };

  const handleClearInput = () => {
    setInputValue('');
    fetchSuggestions('');
    setFocusedOptionIndex(0);
  };

  const handleSuggestionClick = (suggestion: string) => {
    // Fetch more data for the selected suggestion here
    // We can use the Google Places Details API to get more information about the place.
    const detailsService = new google.maps.places.PlacesService(document.createElement('div'));
    detailsService.getDetails({ placeId: suggestion }, (place, status) => {
      if (status === 'OK' && place) {
        const parsedPlace = ParsePlaceResult(place);
        dispatch(setUserSelectedLocation({ place: parsedPlace }));
      } else {
        console.error(`Error fetching place details: ${status}`);
      }
    });
  };

  const handleEnterKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && suggestions.length > 0) {
      // Handle selection of the selected/focused suggestion when user hits 'Enter'
      // If user focused/hovered a selection select that one otherwise select first Option
      if (focusedOptionIndex > 0) {
        handleSuggestionClick(suggestions[focusedOptionIndex].placeId);
      } else {
        handleSuggestionClick(suggestions[0].placeId);
      }
    }
    if (event.key === 'ArrowDown') {
      // Move focus to the next option
      event.preventDefault();
      setFocusedOptionIndex((prevIndex) =>
        prevIndex < suggestions.length - 1 ? prevIndex + 1 : 0,
      );
    } else if (event.key === 'ArrowUp') {
      // Move focus to the previous option
      event.preventDefault();
      setFocusedOptionIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : -1));
    }
  };

  const handleFocusInput = () => {
    // Trigger Fetch Suggestion when the location and input value are already selected
    // And Then User Click/Focus on Input component
    if (inputValue) {
      fetchSuggestions(inputValue);
    }
  };

  // Not for Now
  // To change input value not by input element and then fetching suggestions
  // const handleChangeInputString = (input: string) => {
  //   setInputValue(input);
  //   fetchSuggestions(input);
  // };

  // Not for Now (Mayble Later if Bussiness wants)
  // To make first Option/Suggestion as deafult selected whenever we fetch new data
  // useEffect(() => {
  //   if (suggestions.length) {
  //     setFocusedOptionIndex(0);
  //   }
  // }, [suggestions]);

  return (
    <>
      <InputFieldWrapper>
        <SearchIconContainer>
          <SearchIcon />
        </SearchIconContainer>
        <StyledInput
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleEnterKey}
          onFocus={handleFocusInput}
          error={!suggestions.length && inputValue && error}
          type="text"
          aria-label={t(
            `dealerLocationSearch.searchBar.locationEntryRadiusTab${MARKET === 'CAN' ? 'CA' : 'US'}`,
          )}
          placeholder={t(
            `dealerLocationSearch.searchBar.locationEntryRadiusTab${MARKET === 'CAN' ? 'CA' : 'US'}`,
          )}
        />
        <ClearInputWrapper visible={!!inputValue.length} onClick={handleClearInput}>
          <CancelIcon />
        </ClearInputWrapper>
        {suggestions.length > 0 && (
          <SuggestionsList
            suggestions={suggestions}
            handleSuggestionClick={handleSuggestionClick}
            focusedOptionIndex={focusedOptionIndex}
            setFocusedOptionIndex={setFocusedOptionIndex}
          />
        )}
      </InputFieldWrapper>
      <PlaceHolderForLandscape>
        {t(
          `dealerLocationSearch.searchBar.locationEntryRadiusTab${MARKET === 'CAN' ? 'CA' : 'US'}`,
        )}
      </PlaceHolderForLandscape>
      <ErrorWrapper visible={!suggestions.length && inputValue && error}>
        <CautionIcon />
        {t(`dealerLocationSearch.searchBar.errorInput${MARKET === 'CAN' ? 'CA' : 'US'}`)}
      </ErrorWrapper>
    </>
  );
};

export default AddressAutoComplete;
